import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, authHeader, errMessage } from "../../api";

/*
  Words.

  Every editable line on the home page, the first-visit page and the footer.
  Defaults live in the code; saving a line stores an override, and "Put it
  back" removes it. Nothing here can break a page: a blank field simply falls
  back to the original wording.
*/
export default function CopyPanel() {
  const [fields, setFields] = useState(null);
  const [draft, setDraft] = useState({});
  const [group, setGroup] = useState("The home page");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/copy", { headers: authHeader() });
      setFields(data.fields || []);
      setDraft({});
    } catch (e) {
      setErr(errMessage(e, "We couldn't load the words."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const seen = [];
    (fields || []).forEach((f) => {
      if (!seen.includes(f.group)) seen.push(f.group);
    });
    return seen;
  }, [fields]);

  const shown = (fields || []).filter((f) => f.group === group);
  const dirtyKeys = Object.keys(draft);

  const valueOf = (f) => (f.key in draft ? draft[f.key] : f.value);

  const change = (f, v) => {
    setNote("");
    setDraft((d) => {
      const next = { ...d };
      if (v === f.value) delete next[f.key];
      else next[f.key] = v;
      return next;
    });
  };

  const save = async () => {
    if (!dirtyKeys.length) return;
    setBusy(true);
    setNote("");
    setErr("");
    try {
      const { data } = await api.put(
        "/admin/copy",
        { values: draft },
        { headers: authHeader() },
      );
      const n = (data.saved || 0) + (data.cleared || 0);
      setNote(
        `${n} ${n === 1 ? "line" : "lines"} updated. The site is showing the new wording now.`,
      );
      await load();
    } catch (e) {
      setErr(errMessage(e, "We couldn't save that."));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (keys, everything = false) => {
    setBusy(true);
    setNote("");
    setErr("");
    try {
      await api.post(
        "/admin/copy/reset",
        everything ? { everything: true } : { keys },
        { headers: authHeader() },
      );
      setNote(
        everything
          ? "Every line is back to the original wording."
          : "That line is back to the original wording.",
      );
      await load();
    } catch (e) {
      setErr(errMessage(e, "We couldn't put that back."));
    } finally {
      setBusy(false);
    }
  };

  if (err && !fields) {
    return (
      <section className="admin-sec" data-testid="admin-copy-panel">
        <div className="form-error" data-testid="admin-copy-error">
          {err}{" "}
          <button type="button" className="linkish" onClick={load}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (!fields) {
    return (
      <section className="admin-sec" data-testid="admin-copy-panel">
        <p className="admin-help">Looking&hellip;</p>
      </section>
    );
  }

  const editedCount = fields.filter((f) => f.edited).length;

  return (
    <section className="admin-sec" data-testid="admin-copy-panel">
      <header>
        <h2>Words</h2>
        {editedCount ? (
          <button
            type="button"
            className="linkish"
            onClick={() => reset([], true)}
            disabled={busy}
            data-testid="admin-copy-reset-all-button"
          >
            Put everything back
          </button>
        ) : null}
      </header>
      <p className="admin-help">
        Reword the home page, the first-visit page and the footer without
        touching code. Changes are live the moment you save. Clear a box, or use
        &ldquo;Put it back&rdquo;, and the original wording returns &mdash; so
        nothing here is a one-way door.
        {editedCount ? ` ${editedCount} reworded so far.` : ""}
      </p>

      <nav className="admin-subnav" data-testid="admin-copy-groups">
        {groups.map((g) => (
          <button
            key={g}
            type="button"
            aria-current={group === g}
            onClick={() => setGroup(g)}
            data-testid={`admin-copy-group-${g.replace(/\W+/g, "-").toLowerCase()}`}
          >
            {g}
          </button>
        ))}
      </nav>

      {note ? (
        <div className="notice ok" data-testid="admin-copy-note">
          {note}
        </div>
      ) : null}
      {err ? (
        <div className="form-error" data-testid="admin-copy-save-error">
          {err}
        </div>
      ) : null}

      <div className="copy-fields">
        {shown.map((f) => {
          const v = valueOf(f);
          const dirty = f.key in draft;
          return (
            <div
              className={`copy-field${dirty ? " dirty" : ""}`}
              key={f.key}
              data-testid={`admin-copy-field-${f.key}`}
            >
              <div className="cf-head">
                <label htmlFor={`cf-${f.key}`}>{f.label}</label>
                {f.edited ? (
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => reset([f.key])}
                    disabled={busy}
                    data-testid={`admin-copy-reset-${f.key}`}
                  >
                    Put it back
                  </button>
                ) : null}
              </div>
              {f.kind === "text" ? (
                <textarea
                  id={`cf-${f.key}`}
                  rows={Math.min(8, Math.max(3, Math.ceil(v.length / 70)))}
                  value={v}
                  onChange={(e) => change(f, e.target.value)}
                />
              ) : (
                <input
                  id={`cf-${f.key}`}
                  type="text"
                  value={v}
                  onChange={(e) => change(f, e.target.value)}
                />
              )}
              {f.edited && f.default !== v ? (
                <p className="cf-was">
                  <span className="lab">Originally</span>
                  {f.default}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="admin-savebar" data-testid="admin-copy-savebar">
        <span className="admin-help" style={{ margin: 0 }}>
          {dirtyKeys.length
            ? `${dirtyKeys.length} ${dirtyKeys.length === 1 ? "change" : "changes"} not saved yet.`
            : "Nothing to save."}
        </span>
        <button
          type="button"
          className="act"
          onClick={save}
          disabled={busy || !dirtyKeys.length}
          data-testid="admin-copy-save-button"
        >
          {busy ? "Saving\u2026" : "Save the wording"}
        </button>
        {dirtyKeys.length ? (
          <button
            type="button"
            className="linkish"
            onClick={() => setDraft({})}
            disabled={busy}
            data-testid="admin-copy-discard-button"
          >
            Discard changes
          </button>
        ) : null}
      </div>
    </section>
  );
}
